import { useMemo } from 'react';
import { useQuery } from 'react-apollo';
import flatten from 'lodash.flatten';
import { useParams } from 'react-router-dom';

import { AlbumFolderTags } from '../../../../../graphql/queries/album-folder-tags';
import {
  ALBUM_FOLDER_TAG_GENDER,
  ALBUM_FOLDER_TAG_TYPE,
} from '../../../../../constants/album-folder-tags';
import { capitalize } from '../../../../../util/text';

const getSelectorOptionId = ({ albumFolderTag, level, team }) => {
  return `${albumFolderTag.id}${level.id}${team?.id}`;
};
const getSelectorOptionLabel = ({ albumFolderTag, level, team }) => {
  const genderLabel =
    !albumFolderTag?.gender ||
    albumFolderTag?.gender === ALBUM_FOLDER_TAG_GENDER.ALL
      ? ''
      : `(${
          albumFolderTag.gender === ALBUM_FOLDER_TAG_GENDER.MALE
            ? 'Boys'
            : 'Girls'
        }) `;
  const teamLabel = team ? ` - Team: ${team.name}` : '';

  return `${genderLabel}${albumFolderTag.name} - Level: ${level.name}${teamLabel}`;
};

const getLevelSelectorOptions = ({ albumFolderTag, level }) => {
  if (level.teams.records.length === 0) {
    return [
      {
        id: getSelectorOptionId({ albumFolderTag, level }),
        sport: capitalize((albumFolderTag.name ?? '').toLowerCase().trim()),
        gender: albumFolderTag.gender,
        level: capitalize((level.name ?? '').toLowerCase().trim()),
        // Match the import where team = '' (no team) for this level
        team: '',
        album_folder_tag_id: albumFolderTag.id,
        uniform_album_folder_tag_level_id: level?.id,
        label: getSelectorOptionLabel({ albumFolderTag, level }),
      },
    ];
  }

  return level.teams.records.map(team => ({
    id: getSelectorOptionId({ albumFolderTag, level, team }),
    sport: capitalize((albumFolderTag.name ?? '').toLowerCase().trim()),
    gender: albumFolderTag.gender,
    level: capitalize((level.name ?? '').toLowerCase().trim()),
    team: capitalize((team.name ?? '').toLowerCase().trim()),
    album_folder_tag_id: albumFolderTag.id,
    uniform_album_folder_tag_level_id: level?.id,
    uniform_album_folder_tag_level_team_id: team?.id,
    label: getSelectorOptionLabel({ albumFolderTag, level, team }),
  }));
};

const getAlbumFolderTagSelectorOptions = albumFolderTag => {
  return flatten(
    albumFolderTag.levels.records.map(level =>
      getLevelSelectorOptions({ albumFolderTag, level }),
    ),
  );
};

const useUniformConfigurationSelector = ({ albumFolderId }) => {
  const { folderId } = useParams();
  const finalAlbumFolderId = albumFolderId ?? folderId;
  const { data: { albumFolder } = {} } = useQuery(AlbumFolderTags, {
    variables: {
      id: finalAlbumFolderId,
    },
    skip: !finalAlbumFolderId,
  });

  const selectorOptions = useMemo(() => {
    const { records: tags = [] } = albumFolder?.tags ?? {};
    const possibleSelectionTags = tags.filter(
      tag =>
        tag.type === ALBUM_FOLDER_TAG_TYPE.UNIFORM &&
        tag.levels?.records?.length > 0,
    );

    const unsortedOptions = flatten(
      possibleSelectionTags.map(tag => getAlbumFolderTagSelectorOptions(tag)),
    );

    return unsortedOptions.sort((a, b) => a.label.localeCompare(b.label));
  }, [albumFolder]);

  return {
    albumFolder,
    selectorOptions,
  };
};

export default useUniformConfigurationSelector;
